/* =========================================================
   CATEGORY -> PRODUCT TYPE NESTING (Safe + AJAX-proof)
   - Parent facet: Category metafield (filter.p.m.custom.category)
   - Child facet: Product Type (filter.p.product_type)
   - Admin-driven via window.__FACET_GROUPS__
   - Safeguard: if no matching child LIs exist, DO NOT render expand UI
========================================================= */
(function CategoryProductTypeNesting() {
  const GROUPS = Array.isArray(window.__FACET_GROUPS__) ? window.__FACET_GROUPS__ : [];
  if (!GROUPS.length) return;

  // Adjust these if your filter param names differ
  const PARENT_FACET_PARAM = 'filter.p.m.custom.category';
  const CHILD_FACET_PARAM = 'filter.p.product_type';

  const ROOT_SELECTOR = 'facet-filters-form';
  const FACET_SELECTOR = '.js-filter';
  const LI_SELECTOR = 'li.facets__item';

  const MOVED_ATTR = 'data-facet-nest-moved';
  const PARENT_MARK_ATTR = 'data-facet-has-children';
  const NEST_ATTR = 'data-facet-nest';
  const CHILD_LIST_ATTR = 'data-facet-nest-children';

  const TOGGLE_CLASS = 'facet-group__toggle';
  const CHEVRON_CLASS = 'facet-group__chevron';
  const OPEN_CLASS = 'facet-nesting--open';

  const normalize = (s) => String(s || '').replace(/\s+/g, ' ').trim().toLowerCase();

  function debounce(fn, wait) {
    if (typeof window.debounce === 'function') return window.debounce(fn, wait);
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(null, args), wait);
    };
  }

  function findFacetByParam(root, paramName) {
    const facets = root.querySelectorAll(`${FACET_SELECTOR}[id^="Filter-"]`);
    // We can detect by looking at any checkbox name inside the facet list
    for (const facet of facets) {
      const anyInput = facet.querySelector(`input[type="checkbox"][name="${CSS.escape(paramName)}"]`);
      if (anyInput) return facet;
    }
    return null;
  }

  function getLabelElFromLi(li) {
    // Your theme uses <input class="product-option__input"> + <label for="...">
    return li ? li.querySelector('label') : null;
  }

  function getValueLabelFromLi(li) {
    const label = getLabelElFromLi(li);
    return label ? normalize(label.textContent) : '';
  }

  function findValueLiByLabel(facetEl, optionLabel) {
    if (!facetEl || !optionLabel) return null;
    const wanted = normalize(optionLabel);
    const lis = facetEl.querySelectorAll(LI_SELECTOR);
    for (const li of lis) {
      if (getValueLabelFromLi(li) === wanted) return li;
    }
    return null;
  }

  function ensureNestedContainer(parentLi) {
    let nest = parentLi.querySelector(`[${NEST_ATTR}]`);
    if (nest) return nest;

    nest = document.createElement('div');
    nest.setAttribute(NEST_ATTR, '');
    nest.innerHTML = `
      <div class="facet-group">
        <ul class="facets__list list-unstyled facet-group__list" ${CHILD_LIST_ATTR}></ul>
      </div>
    `;
    parentLi.appendChild(nest);
    return nest;
  }

  function ensureToggle(parentLi) {
    // Toggle sits next to the label, but parent checkbox remains native/selectable.
    let btn = parentLi.querySelector(`button.${TOGGLE_CLASS}`);
    if (btn) return btn;

    const labelEl = getLabelElFromLi(parentLi);
    if (!labelEl) return null;

    btn = document.createElement('button');
    btn.type = 'button';
    btn.className = TOGGLE_CLASS;
    btn.setAttribute('aria-expanded', 'false');
    btn.innerHTML = `<span class="${CHEVRON_CLASS}" aria-hidden="true">▾</span>`;

    // Put the chevron right after the label text
    labelEl.insertAdjacentElement('afterend', btn);

    btn.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();

      const isOpen = parentLi.classList.toggle(OPEN_CLASS);
      btn.setAttribute('aria-expanded', isOpen ? 'true' : 'false');

      const nest = parentLi.querySelector(`[${NEST_ATTR}]`);
      if (nest) nest.hidden = !isOpen;
    });

    return btn;
  }

  function cleanupParentUi(parentLi) {
    // Remove nested UI + toggle if present (for misconfigured / no children matches)
    parentLi.removeAttribute(PARENT_MARK_ATTR);
    parentLi.classList.remove(OPEN_CLASS);

    const toggle = parentLi.querySelector(`button.${TOGGLE_CLASS}`);
    if (toggle) toggle.remove();

    const nest = parentLi.querySelector(`[${NEST_ATTR}]`);
    if (nest) nest.remove();
  }

  function applyGroups(root) {
    const parentFacet = findFacetByParam(root, PARENT_FACET_PARAM);
    const childFacet = findFacetByParam(root, CHILD_FACET_PARAM);
    if (!parentFacet || !childFacet) return;

    GROUPS.forEach((group) => {
      if (!group || !group.parentLabel || !Array.isArray(group.childLabels)) return;

      const parentLi = findValueLiByLabel(parentFacet, group.parentLabel);
      if (!parentLi) return;

      // Collect existing child <li> elements that actually exist right now
      const childLis = [];
      group.childLabels.forEach((childLabel) => {
        const childLi = findValueLiByLabel(childFacet, childLabel);
        if (childLi) childLis.push(childLi);
      });

      // ✅ SAFEGUARD: if nothing matched, keep parent row native (no blank expand UI)
      if (!childLis.length) {
        cleanupParentUi(parentLi);
        return;
      }

      // Create UI (toggle + nest)
      parentLi.setAttribute(PARENT_MARK_ATTR, 'true');

      const nest = ensureNestedContainer(parentLi);
      const childList = nest.querySelector(`[${CHILD_LIST_ATTR}]`);
      if (!childList) return;

      const toggle = ensureToggle(parentLi);
      if (!toggle) return;

      // Default: collapsed
      const isOpen = parentLi.classList.contains(OPEN_CLASS);
      nest.hidden = !isOpen;
      toggle.setAttribute('aria-expanded', isOpen ? 'true' : 'false');

      // Move children into the nested list (idempotent by attr guard)
      childLis.forEach((childLi) => {
        if (childLi.hasAttribute(MOVED_ATTR)) return;
        childLi.setAttribute(MOVED_ATTR, group.parentLabel);
        childLi.classList.add('facet-group__item');
        childList.appendChild(childLi);
      });
    });
  }

  function initAll() {
    document.querySelectorAll(ROOT_SELECTOR).forEach((root) => {
      // Only run if facets exist in this root
      if (!root.querySelector(FACET_SELECTOR)) return;
      applyGroups(root);
    });
  }

  // Initial run
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Re-run on Shopify AJAX updates (facets re-render)
  const target = document.querySelector(ROOT_SELECTOR) || document.body;
  const rerun = debounce(initAll, 50);
  const observer = new MutationObserver(() => rerun());
  observer.observe(target, { childList: true, subtree: true });
})();

/* =========================================================
   FACET UI TWEAKS (Color: 2 columns + no show more)
                 (Size: button grid styling)
   Runs on load + on AJAX facet refresh (MutationObserver)
========================================================= */
(function FacetUiTweaks() {
  const ROOT_SELECTOR = 'facet-filters-form';
  const FACET_SELECTOR = '.js-filter';
  const LI_SELECTOR = 'li.facets__item';

  const normalize = (s) => String(s || '').toLowerCase();

  function debounce(fn, wait) {
    if (typeof window.debounce === 'function') return window.debounce(fn, wait);
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(null, args), wait);
    };
  }

  function markFacetTypes(root) {
    const facets = root.querySelectorAll(FACET_SELECTOR);

    facets.forEach((facet) => {
      facet.classList.remove('facet--color', 'facet--size');

      const inputs = facet.querySelectorAll('input[type="checkbox"][name]');
      let isColor = false;
      let isSize = false;

      inputs.forEach((inp) => {
        const name = normalize(inp.getAttribute('name'));
            // Variant options (filter.v.option.*)
            if (name.includes('filter.v.option')) {
            if (name.includes('option.color') || name.includes('option.colour')) isColor = true;
            if (name.includes('option.size')) isSize = true;
            }

            // Shopify taxonomy colour (filter.v.t.shopify.color-pattern / color)
            if (name.includes('filter.v.t.shopify.color')) {
            isColor = true;
            }
      });

      if (isColor) facet.classList.add('facet--color');
      if (isSize) facet.classList.add('facet--size');
    });
  }

  function applyColorRules(root) {
    root.querySelectorAll(`${FACET_SELECTOR}.facet--color`).forEach((facet) => {
      // Hide "show more" button (if theme adds it)
      const btn = facet.querySelector('button.show-more');
      if (btn) btn.style.display = 'none';

      // Ensure nothing is collapsed
      facet.querySelectorAll(`${LI_SELECTOR}.is-collapsed`).forEach((li) => li.classList.remove('is-collapsed'));
    });
  }

  function applySizeRules(root) {
    root.querySelectorAll(`${FACET_SELECTOR}.facet--size`).forEach((facet) => {
      // Hide "show more" for size too (usually size lists should be fully visible)
      const btn = facet.querySelector('button.show-more');
      if (btn) btn.style.display = 'none';

      // Ensure nothing is collapsed
      facet.querySelectorAll(`${LI_SELECTOR}.is-collapsed`).forEach((li) => li.classList.remove('is-collapsed'));

      // Add a helper class on each size item so CSS can style label like a button
      facet.querySelectorAll(LI_SELECTOR).forEach((li) => {
        li.classList.add('facet-size__item');
      });
    });
  }

  document.addEventListener("DOMContentLoaded", () => {
  document
    .querySelectorAll('.js-filter[data-filter-param="filter.p.product_type"]')
    .forEach((el) => {
      const details = el.closest("details.facets__accordion-section");
      if (details) details.style.display = "none";
    });
});

  function run() {
    document.querySelectorAll(ROOT_SELECTOR).forEach((root) => {
      if (!root.querySelector(FACET_SELECTOR)) return;

      markFacetTypes(root);
      applyColorRules(root);
      applySizeRules(root);
    });
  }

  // initial
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }

  // re-run on AJAX changes
  const target = document.querySelector(ROOT_SELECTOR) || document.body;
  const rerun = debounce(run, 50);
  const observer = new MutationObserver(() => rerun());
  observer.observe(target, { childList: true, subtree: true });
})();

/* =========================================================
   PATCH 011: PRODUCT-CARD SLIDER - FLUID DRAG, NO SNAP
   - Monkey-patches <swiper-component>'s init() (defined in global.js)
     rather than editing global.js directly. This is a large vendor
     file that is easy to mis-transcribe and, per the changelog's own
     "UPDATE PROCESS CHECKLIST", is NOT one of the files manually
     carried forward on every theme duplication - editing it in place
     is exactly how the original PATCH 005/006/007 tuning was lost to
     a later theme update. custom.js IS carried forward wholesale, so
     keeping the fix here as a single self-contained override makes it
     far more resilient to future theme updates.
   - Scoped to sliders with the "card-product-slider" class only
     (product recommendation carousels). Hero banners, testimonial
     sliders, and other <swiper-component> instances elsewhere in the
     theme are unaffected and keep their existing snap-to-slide
     behaviour - this scoping is a deliberate judgment call, not
     something explicitly requested, flagged here for visibility.
   - followFinger: true makes the slide track the pointer in real time
     during drag (instead of only jumping on release).
   - freeMode gives free-flowing momentum scrolling with no snap back
     to a slide boundary on release. NOTE: Swiper was upgraded to
     11.1.14 (see assets/swiper-bundle.min.js header) at some point
     after PATCH 007 was written; that patch's flat freeMode* /
     momentum* properties are a pre-11 API and are silently ignored by
     this version, which is part of why the earlier tuning being
     reverted went unnoticed. This uses Swiper 11's nested freeMode
     object instead.
   - This function's body is a faithful copy of global.js's current
     SwiperComponent.prototype.init(), with only the two additions
     above layered on for product-card sliders. If Shopify ever
     updates global.js's init() logic itself, this override needs to
     be re-synced by hand.
========================================================= */
(function PatchSwiperComponentForProductSliders() {
  const SwiperComponentClass = customElements.get('swiper-component');
  if (!SwiperComponentClass) return;

  SwiperComponentClass.prototype.init = function () {
    if (this.swiperInitialized) return;
    this.swiperInitialized = true;

    const swiperOptions = this.swiperOptions;
    const isProductSlider = this.classList.contains('card-product-slider');

    const option = {
      slideshowId: swiperOptions.swiperId || "",
      slideCount: swiperOptions.slideCount || 1,
      slidesPerView: swiperOptions.slidesPerView || "auto",
      slidesPerViewDesktop:
        swiperOptions.slidesPerViewDesktop ||
        swiperOptions.slidesPerView ||
        4,
      slidesPerViewTablet:
        swiperOptions.slidesPerViewTablet ||
        swiperOptions.slidesPerViewDesktop ||
        swiperOptions.slidesPerView ||
        3,
      spaceBetween: swiperOptions.spaceBetweenMobile || 0,
      spaceBetweenDesktop:
        swiperOptions.spaceBetweenDesktop ||
        swiperOptions.spaceBetween ||
        0,
      spaceBetweenTablet:
        swiperOptions.spaceBetweenTablet ||
        swiperOptions.spaceBetweenDesktop ||
        swiperOptions.spaceBetweenMobile ||
        0,
      enabled: swiperOptions.enabled ?? true,
      enabledDesktop:
        swiperOptions.enabledDesktop ?? swiperOptions.enabled ?? true,
      enabledTablet:
        swiperOptions.enabledTablet ??
        swiperOptions.enabledDesktop ??
        swiperOptions.enabled ??
        true,
      allowTouchMove:
        swiperOptions.allowTouchMove ??
        (typeof swiperOptions.slidesPerView === "number"
          ? swiperOptions.slideCount > swiperOptions.slidesPerView
            ? true
            : false
          : true),
      allowTouchMoveTablet:
        swiperOptions.allowTouchMoveTablet ??
        (typeof swiperOptions.slidesPerViewTablet === "number"
          ? swiperOptions.slideCount > swiperOptions.slidesPerViewTablet
            ? true
            : false
          : true),
      allowTouchMoveDesktop:
        swiperOptions.allowTouchMoveDesktop ??
        (typeof swiperOptions.slidesPerViewDesktop === "number"
          ? swiperOptions.slideCount > swiperOptions.slidesPerViewDesktop
            ? true
            : false
          : true)
    };

    this.swiperOptions = {
      a11y: false,
      allowTouchMove: option.allowTouchMove,
      loop: swiperOptions.loop || false,
      rewind: swiperOptions.rewind || false,
      followFinger: isProductSlider ? true : (swiperOptions.followFinger || false),
      cssMode: swiperOptions.cssMode || false,
      enabled: option.enabled,
      watchOverflow: true,
      observer: true,
      cache: true,
      slidesPerView: option.slidesPerView,
      spaceBetween: option.spaceBetween,
      ...(isProductSlider && {
        threshold: 1,
        preventClicks: true,
        preventClicksPropagation: true,
        freeMode: {
          enabled: true,
          sticky: false,
          momentumRatio: 1.5,
          momentumVelocityRatio: 1,
          momentumBounce: false
        }
      }),
      breakpoints: {
        750: {
          enabled: option.enabledTablet,
          allowTouchMove: option.allowTouchMoveTablet,
          slidesPerView: option.slidesPerViewTablet,
          spaceBetween: option.spaceBetweenTablet
        },
        990: {
          enabled: option.enabledDesktop,
          allowTouchMove: option.allowTouchMoveDesktop,
          slidesPerView: option.slidesPerViewDesktop,
          spaceBetween: option.spaceBetweenDesktop
        }
      }
    };

    if (swiperOptions.navigation) {
      this.swiperOptions.navigation = { ...swiperOptions.navigation };
    }

    if (swiperOptions.effect == "fade") {
      this.swiperOptions.effect = "fade";
      this.swiperOptions.fadeEffect = { crossFade: true };
    }

    if (swiperOptions.pagination == "custom_bullet") {
      this.swiperOptions.pagination = {
        el: swiperOptions.paginationElement,
        clickable: true,
        renderBullet: function (i, className) {
          return `
            <button class="${className}">
              <span></span>
            </button>
          `;
        }
      };
    } else {
      this.swiperOptions.pagination = {
        el: `.swiper-pagination--${swiperOptions.swiperId}`,
        clickable: true
      };
    }

    if (swiperOptions.autoplay) {
      this.swiperOptions.autoplay = { ...swiperOptions.autoplay };

      if (swiperOptions.autoplayProgress) {
        const progressCircle = this.querySelector(
          `.autoplay-progress--${swiperOptions.swiperId} svg`
        );
        const progressContent = this.querySelector(
          `.autoplay-progress--${swiperOptions.swiperId} span`
        );

        this.swiperOptions.on = {
          autoplayTimeLeft(s, time, progress) {
            progressCircle.style.setProperty("--progress", 1 - progress);
            progressContent.textContent = `${Math.ceil(time / 1000)}`;
          },
        };
      }
    }

    this.swiper = new Swiper(this, {
      ...this.swiperOptions
    });
  };
})();